import { t } from 'i18next';
import { useLiberisContext, LiberisStage } from '@repo/app-contexts/liberis';
import { areAllProviderNotPromoted } from '@repo/store/helpers';
import { useProviderStore } from '@repo/store/provider';
import { useEventClick } from '@repo/hooks';

import { LiberisStageWrapper } from '@/components/welcome/loan-application-possible/liberis/common/components/liberis-stage-wrapper';
// AMOUNT
import { LiberisAmountForm } from '@/components/welcome/loan-application-possible/liberis/amount/liberis-amount-form';
import { LiberisAmountFormTitle } from '@/components/welcome/loan-application-possible/liberis/amount/liberis-amount-form-title';
// CHECK_YOUR_DATA
import { LiberisCheckYourData } from '@/components/welcome/loan-application-possible/liberis/check-your-data/liberis-check-your-data';
import { LiberisCheckYourDataTitle } from '@/components/welcome/loan-application-possible/liberis/check-your-data/liberis-check-your-data-title';
// AGREEMENTS
import { LiberisAgreementsForm } from '@/components/welcome/loan-application-possible/liberis/agreements-liberis/liberis-agreements-form';
import { LiberisAgreementsFormTitle } from '@/components/welcome/loan-application-possible/liberis/agreements-liberis/liberis-agreements-form-title';
// COMPANY DATA
import { LiberisCompanyData } from '@/components/welcome/loan-application-possible/liberis/company-data/liberis-company-data';
import { LiberisCompanyDataTitle } from '@/components/welcome/loan-application-possible/liberis/company-data/liberis-company-data-title';
// BENEFICIENT DATA
import { LiberisBeneficientDataTitle } from '@/components/welcome/loan-application-possible/liberis/beneficient-data/liberis-beneficient-data-title';
import { LiberisBeneficientData } from '@/components/welcome/loan-application-possible/liberis/beneficient-data/liberis-beneficient-data';
import { useMainHubStoreState } from '@/store/main-hub.store';
import { isMerchantContactDataEmpty } from '@/components/welcome/loan-application-possible/liberis/common/helpers/is-merchant-contact-data-empty';
import { completeDataLink } from '@/components/welcome/loan-application-possible/liberis/common/helpers/complete-data-link';
import { companyType } from '@/components/loan-form/schema/loan-form.schema';
import { RequestedLoanValuesWithP24Agreements } from '@/components/loan-offer-data-process/form/form.schema';
import { StageHeader } from '@/components/loan-offer-data-process/stage-header';

export const renderLoanApplicationPossibleLiberisStage = (
  stage: LiberisStage,
  initialValues: RequestedLoanValuesWithP24Agreements,
  options: { withWrapper?: boolean; maxLiberisAmount?: number } = {
    withWrapper: true,
    maxLiberisAmount: undefined,
  },
) => {
  const { setLiberisStage, setActiveBeneficientIndex, activeBeneficientIndex } =
    useLiberisContext();
  const { merchantResponseData } = useMainHubStoreState();
  const {
    state: { isAvailableMultiprovider },
  } = useProviderStore();

  const trackClick = useEventClick(merchantResponseData);

  const merchantDetails = merchantResponseData?.data?.merchantData;
  const selectedCompanyType = companyType(
    merchantResponseData?.data?.merchantData?.companyType || 'jednoosobowa',
  );
  const isJdg = selectedCompanyType === 'jednoosobowa';

  const isLastBeneficient = activeBeneficientIndex + 1 === initialValues.person.length;
  const isFirstBeneficient = activeBeneficientIndex === 0;

  const notPromotedAnyProvider =
    merchantResponseData && areAllProviderNotPromoted(merchantResponseData);

  const withWrapper = options.withWrapper !== false;
  const maxLiberisAmount = options?.maxLiberisAmount;

  switch (stage) {
    case 'AMOUNT':
      return {
        component: withWrapper ? (
          <LiberisStageWrapper isVerticalHeader={true} title={<LiberisAmountFormTitle />}>
            <LiberisAmountForm />
          </LiberisStageWrapper>
        ) : (
          <>
            <StageHeader isVerticalHeader={true} title={<LiberisAmountFormTitle />} />
            <LiberisAmountForm />
          </>
        ),
        buttonText: t('welcomePage.loanApplicationPossible.liberis.amountForm.buttonText'),
        onButtonClick: () => {
          if (isMerchantContactDataEmpty(merchantDetails)) {
            setLiberisStage('CHECK_YOUR_DATA');
          } else {
            setLiberisStage('AGREEMENTS');
          }
        },
        withSubmit: isAvailableMultiprovider && notPromotedAnyProvider,
      };
    case 'CHECK_YOUR_DATA':
      return {
        component: withWrapper ? (
          <LiberisStageWrapper isVerticalHeader={true} title={<LiberisCheckYourDataTitle />}>
            <LiberisCheckYourData />
          </LiberisStageWrapper>
        ) : (
          <>
            <StageHeader isVerticalHeader={true} title={<LiberisCheckYourDataTitle />} />
            <LiberisCheckYourData />
          </>
        ),
        buttonText: t('welcomePage.loanApplicationPossible.liberis.checkYourData.buttonText'),
        onButtonClick: () => {
          window.open(completeDataLink(merchantDetails), '_blank');
        },
        withMarketingAgreementForm: false,
      };
    case 'AGREEMENTS':
      return {
        component: withWrapper ? (
          <LiberisStageWrapper
            title={<LiberisAgreementsFormTitle />}
            titleWrapperSx={{
              marginBottom: { xs: '14px', sm: '6px', md: '12px' },
              paddingLeft: 0,
            }}
            onBackButtonClick={() => setLiberisStage('AMOUNT')}
          >
            <LiberisAgreementsForm />
          </LiberisStageWrapper>
        ) : (
          <>
            <StageHeader
              title={
                <LiberisAgreementsFormTitle isMultiprovider={true} amount={maxLiberisAmount} />
              }
              titleWrapperSx={{
                marginBottom: { xs: '14px', sm: '6px', md: '12px' },
                paddingLeft: 0,
              }}
            />
            <LiberisAgreementsForm isLiberisAgreementsModal={true} />
          </>
        ),
        buttonText: t('welcomePage.loanApplicationPossible.liberis.agreementsForm.buttonText'),
        onButtonClick: () => {
          trackClick({
            activity: 'LIBERIS_CONSENTS',
            previousActivity: 'OFFERS_VIEW',
            nextActivity: 'LIBERIS_COMPANY_DETAILS',
          });
          setLiberisStage('COMPANY_DATA');
        },
        withMarketingAgreementForm: false,
      };
    case 'COMPANY_DATA':
      return {
        component: withWrapper ? (
          <LiberisStageWrapper
            title={<LiberisCompanyDataTitle />}
            titleWrapperSx={{
              paddingLeft: 0,
            }}
            onBackButtonClick={() => setLiberisStage('AGREEMENTS')}
          >
            <LiberisCompanyData merchantDetails={merchantDetails} />
          </LiberisStageWrapper>
        ) : (
          <>
            <StageHeader
              title={<LiberisCompanyDataTitle />}
              titleWrapperSx={{
                paddingLeft: 0,
              }}
              onBackButtonClick={() => setLiberisStage('AGREEMENTS')}
            />
            <LiberisCompanyData merchantDetails={merchantDetails} />
          </>
        ),
        buttonText: t('welcomePage.loanApplicationPossible.liberis.companyData.buttonText'),
        onButtonClick: () => {
          trackClick({
            activity: 'LIBERIS_COMPANY_DETAILS',
            previousActivity: 'LIBERIS_CONSENTS',
            nextActivity: 'LIBERIS_PERSONS_DETAILS',
          });
          setLiberisStage('BENEFICIENT_DATA');
        },
      };
    case 'BENEFICIENT_DATA':
      return {
        component: withWrapper ? (
          <LiberisStageWrapper
            title={<LiberisBeneficientDataTitle />}
            onBackButtonClick={() =>
              isJdg || isFirstBeneficient
                ? setLiberisStage('COMPANY_DATA')
                : setActiveBeneficientIndex((prevIndex) => prevIndex - 1)
            }
          >
            <LiberisBeneficientData />
          </LiberisStageWrapper>
        ) : (
          <>
            <StageHeader
              title={<LiberisBeneficientDataTitle />}
              onBackButtonClick={() =>
                isJdg || isFirstBeneficient
                  ? setLiberisStage('COMPANY_DATA')
                  : setActiveBeneficientIndex((prevIndex) => prevIndex - 1)
              }
            />
            <LiberisBeneficientData />
          </>
        ),
        buttonText: t('welcomePage.loanApplicationPossible.liberis.beneficientData.buttonText'),
        withSubmit: isJdg ? true : isLastBeneficient,
        onButtonClick: () => {
          setActiveBeneficientIndex((prevIndex) => prevIndex + 1);
        },
        onSubmit: () => {
          trackClick({
            activity: 'LIBERIS_PERSONS_DETAILS',
            previousActivity: 'LIBERIS_COMPANY_DETAILS',
            nextActivity: 'OFFERS_VIEW',
          });
        },
      };
    default:
      return null;
  }
};
